<script setup lang="ts">
import { computed, reactive,  watch } from 'vue';
import CInput from '@/components/shared/CInput.vue';
import { ProductBase } from '@/typings/customilyElements/productBase';
import CSelect,  { type CSelectItem, type Primitive } from '@/components/shared/CSelect';
import CButton from '@/components/shared/CButton';


const props = defineProps<{
    productBase?: ProductBase;
}>();

const emit = defineEmits<{
    (e: 'edit-production-file'): void;
    (e: 'edit-preview'): void;
    (e: 'color-variant-selected', position: number): void;
    (e: 'generate-color-images'): void;
    (e: 'save-product-base'): void;
}>();

const model = reactive({
    fileWidth: 0,
    fileHeight: 0,
    unit: '',
    dpi: 0,
    format: '',
    variants: [] as { name: string, values: string[], isColorVariant: boolean, selected?: string }[] 
});

const hasColorVariant = computed(() => {
    return props.productBase?.variants?.some(v => v.isColorVariant) ?? false;
});

const initModel = (productBase: ProductBase) => {
    model.fileWidth = productBase.fileWidth;
    model.fileHeight = productBase.fileHeight;
    model.unit = productBase.unit;
    model.dpi = productBase.dpi;
    model.format = productBase.format;
    model.variants = productBase.variants.map(v => {
    const hasValues = v.values?.length > 0;
        return {
            name: v.name,
            values: v.values ?? [],
            isColorVariant: v.isColorVariant || false,
            selected: hasValues ? v.values[0] : undefined
        };
    });
};

watch(() => props.productBase, (element) => {
    if (!element) {
        return;
    }
    initModel(element);
}, { immediate: true, deep: true  });

const editProductionFile = () => {
    emit('edit-production-file');
};

const editPreview = () => {
    emit('edit-preview');
};

const generateColorImages = () => {
    emit('generate-color-images');
};

const save = () => {
    emit('save-product-base');
};

const selectVariant = (isColorVariant: boolean, item: CSelectItem<Primitive> | Primitive | undefined) => {
    if (!isColorVariant) {
        return;
    }

    if (!item || typeof item !== "object") {
        return;
    }
    const position = (item as CSelectItem<string, {position: number}>)?.object?.position ?? 1;
    emit('color-variant-selected', position);
};

</script>

<template>
    <div class="text-character-wrapper">
        <div class="row">
            <div class="col-12">
                <label class="text-strong ">Production File</label>
            </div>
        </div>
        <div class="row">
            <div class="col-4">
                <c-input
                    v-model="model.fileWidth"
                    class="flex-fill"
                    label="Width"
                    :step="1"
                    type="decimal"
                    :disabled="true"
                    :fraction-digits="2"
                />
            </div>
            <div class="col-4">
                <c-input
                    v-model="model.fileHeight"
                    class="flex-fill"
                    label="Height"
                    :step="1"
                    type="decimal"
                    :disabled="true"
                    :fraction-digits="2"
                />
            </div>
            <div class="col-4">
                <c-input
                    v-model="model.unit"
                    class="flex-fill"
                    label="Unit"
                    type="text"
                    :disabled="true"
                />
            </div>
        </div>
        <div class="row">
            <div class="col-5">
                <c-input
                    v-model="model.format"
                    class="flex-fill"
                    label="Export Format"
                    type="text"
                    :disabled="true"
                />
            </div>
            <div class="col-7">
                <c-input
                    v-model="model.dpi"
                    class="flex-fill"
                    label="Export Resolution"
                    type="text"
                    :disabled="true"
                />
            </div>
        </div>
        <div class="row" style="padding-top: 10px; padding-bottom: 15px;">
            <div class="col-12">
                <c-button
                    style="width: 100%;"
                    variant="outline-primary"
                    text="Edit Production File" 
                    @click="editProductionFile"
                />
            </div>
        </div>
        <div class="row">
            <div class="col-12">
                <label class="text-strong ">Preview & Variants</label>
            </div>
        </div>
        <div class="row" v-for="(variant, index) in model.variants" :key="index">
            <div class="col-12">
                <c-select
                    v-model="variant.selected"
                    :options="variant.values.map((v, i) => ({ label: v, value: v, object: {position: i + 1} }))"
                    :label="variant.name"
                    @input="option => selectVariant(variant.isColorVariant, option)"
                />
            </div>
        </div>
        <div v-if="hasColorVariant" class="row" style="padding-top: 10px;">
            <div class="col-12">
                <c-button
                    style="width: 100%;"
                    variant="outline-primary"
                    text="Generate Color Images"
                    @click="generateColorImages"
                />
            </div>
        </div>
        <div class="row" style="padding-top: 10px;">
            <div class="col-12">
                <c-button
                    style="width: 100%;"
                    variant="outline-primary"
                    text="Edit Preview"
                    @click="editPreview"
                />
            </div>
        </div>
        <div class="row" style="padding-top: 20px;">
            <div class="col-12">
                <c-button
                    style="width: 100%;"
                    variant="primary"
                    text="Save"
                    @click="save"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.text-character-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
}
.text-strong {
    font-weight: 700;
    font-size: 0.975rem;
}
</style>
